import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LicensureDetails } from 'src/app/compliance/models/licensure-details.model';
import { StateLicensureGridItem } from 'src/app/compliance/state-licensure/grid/models/state-licensure-grid-item.model';
import { StateLicensureGridLookups } from 'src/app/compliance/state-licensure/grid/models/state-licensure-grid-lookups.model';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { FetchArgs, PagingToken } from 'src/app/shared/models/core';
import { ListItem } from 'src/app/shared/models/list-item.model';
import { combine } from 'src/app/shared/utilities/route';
import { StateLicensureAttachment } from '../grid/models/state-licensure-details.model';
import { Owner } from 'src/app/shared/helpers/owner.decorator';

/**
 * @deprecated Do not use this service.  Use service(s) from "src/app/compliance/api" or "src/app/compliance/api/migration"
 */
@Owner('Mic Drop')
@Injectable({ providedIn: 'root' })
export class StateLicensureService {
    private coreApiPath: string;

    constructor(
        @Inject(APP_CONFIG) settings: Settings,
        private http: HttpClient
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
    }

    // ** StateLicensureController.SearchPostAsync
    searchStateLicensure(args: FetchArgs): Observable<PagingToken<StateLicensureGridItem[]>> {
        const url = combine(this.coreApiPath, 'state-licensure');
        return this.postPaged(url, args);
    }

    // ** StateLicensureController.GridLookups
    getGridLookups(): Observable<StateLicensureGridLookups> {
        const url = combine(this.coreApiPath, 'state-licensure/lookup');
        return this.http.get<StateLicensureGridLookups>(url);
    }

    // ** StateLicensureController.GetDetailsByState
    getDetails(stateId): Observable<LicensureDetails[]> {
        const url = combine(this.coreApiPath, `state-licensure/${stateId}/details`);
        return this.http.get<LicensureDetails[]>(url);
    }

    // ** StateLicensureController.GetFilesByState
    getAttachments(stateId): Observable<StateLicensureAttachment[]> {
        const url = combine(this.coreApiPath, `state-licensure/${stateId}/files`);
        return this.http.get<StateLicensureAttachment[]>(url);
    }

    // ** StateLicensureController.GetFile
    downloadAttachments(id: number, stateId: number): Observable<Blob> {
        const url = combine(this.coreApiPath, `state-licensure/${stateId}/files/${id}`);
        return this.http.get(url, { responseType: 'blob' });
    }

    // ** StateLicensureController.DeleteFile
    deleteAttachment(id: number, stateId: number) {
        const url = combine(this.coreApiPath, `state-licensure/${stateId}/files/${id}`);
        return this.http.delete<any>(url);
    }

    // ** StateLicensureController.GetProjectTypes
    getProjectTypes(): Observable<ListItem[]> {
        const url = combine(this.coreApiPath, `state-licensure/project-types`);
        return this.http.get<ListItem[]>(url);
    }

    // ** StateLicensureController.UpdateDetail
    updateLic(data: LicensureDetails, id: number, stateId: number): Observable<unknown> {
        const url = combine(this.coreApiPath, `state-licensure/${stateId}/details/${id}`);
        return this.http.put<unknown>(url, data);
    }

    // ** StateLicensureController.AddFile
    uploadFile(stateId: number, file: any): Observable<any> {
        const url = combine(this.coreApiPath, `state-licensure/${stateId}/files`);
        const data = new FormData();
        data.append('file', file);
        if (file.type) {
            data.append('type', file.type.toString());
        }
        return this.http.post<any>(url, data);
    }

    // ** StateLicensureController.GetAdmins
    getAdmins(): Observable<ListItem[]> {
        const url = combine(this.coreApiPath, `state-licensure/admins`);
        return this.http.get<ListItem[]>(url);
    }

    protected postPaged<T>(url: string, fetchArgs: FetchArgs): Observable<PagingToken<T[]>> {
        return this.http.post<PagingToken<T[]>>(url, fetchArgs, { observe: 'response' }).pipe(
            map((response) => {
                return {
                    total: response.body.total,
                    data: response.body.data
                };
            })
        );
    }
}
